import { Component, ElementRef, ViewChild } from '@angular/core';
import { MatLegacyMenuTrigger as MatMenuTrigger } from '@angular/material/legacy-menu';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { connectState, getTrackByFn } from '@y42/angular/util';
import { DateTimeFormat, DateTimeService } from '@y42/date-time';
import { ErrorHandlerService } from '@y42/error-handling';
import { ColumnTypeEnum, OperationModel, OperatorEnum } from '@y42/json-schema';
import { DistributionModel } from '@y42/model';
import { isNotNullish } from '@y42/util/nullish';
import { IHeaderAngularComp } from 'ag-grid-angular';
import { ICellRendererParams, IFilter, IFilterParams, IHeaderParams } from 'ag-grid-community';
import sortBy from 'lodash/sortBy';
import { BehaviorSubject, map } from 'rxjs';
import { DatasourceColumnUIModel } from '../../state';

interface HeaderRendererComponentParams extends ICellRendererParams, IHeaderParams {
  dataColumn: DatasourceColumnUIModel;
  column: IFilterParams['column'];
  distributions?: DistributionModel[];
  distinct: number;
  loading?: boolean;
  activeHeaderColumn?: DatasourceColumnUIModel;
  showAnalyzeColumnBtn?: boolean;
  hideHeaderGraph?: boolean;
}

interface EnrichedDistributionModel extends DistributionModel {
  isActive: boolean;
  tooltip: string;
  style: { height: string };
}

@UntilDestroy()
@Component({
  selector: 'y42-header-renderer',
  templateUrl: './header-renderer.component.html',
  styleUrls: ['./header-renderer.component.scss'],
})
export class HeaderRendererComponent implements IHeaderAngularComp {
  constructor(
    private readonly dateTimeService: DateTimeService,
    private readonly errorHandlerService: ErrorHandlerService,
  ) {}

  private readonly sorted$ = new BehaviorSubject<'desc' | 'asc' | null>(null);
  private readonly sortedDistributionsDesc$ = new BehaviorSubject<DistributionModel[]>([]);

  private readonly filterActive$ = new BehaviorSubject<boolean>(false);
  private activeFilterValues: Array<DistributionModel['value'] | null> = [];
  private activeFilterInstance?: IFilter | null;

  public params!: HeaderRendererComponentParams;

  @ViewChild('trigger') menu?: MatMenuTrigger;
  @ViewChild('customHeaderMenuButton', { static: false, read: ElementRef }) customHeaderMenuButton?: ElementRef;

  public readonly trackByValue = getTrackByFn<EnrichedDistributionModel>('value');

  readonly state = connectState(this, {
    sorted: this.sorted$,
    sortedIcon: this.sorted$.pipe(
      map((sortedVal) => {
        if (sortedVal === 'desc') {
          return ['fal', 'arrow-down'] as IconProp;
        } else if (sortedVal === 'asc') {
          return ['fal', 'arrow-up'] as IconProp;
        }
        return null;
      }),
    ),
    sortedDistributionsDesc: this.sortedDistributionsDesc$.pipe(
      map((distributions) => {
        let maxValue = 0;
        const firstDistribution = distributions[0];
        if (firstDistribution) {
          maxValue = firstDistribution.sample_size;
        }

        return distributions.map((distribiution) => ({
          ...distribiution,
          isActive: this.checkActive(distribiution.value),
          tooltip: this.tooltipValueFormatter(distribiution),
          style: {
            height: maxValue === 0 ? '100%' : `${Math.round((distribiution.sample_size / maxValue) * 100)}%`,
          },
        }));
      }),
    ),
    filterActive: this.filterActive$,
  });

  agInit(params: HeaderRendererComponentParams) {
    this.params = params;
    this.params.column.addEventListener('sortChanged', this.onSortChanged.bind(this));
    this.params.column.addEventListener('filterChanged', this.onFilterChanged.bind(this));
    this.onSortChanged();

    this.filterActive$.next(this.params.column.isFilterActive());
    this.activeFilterInstance = this.params.api.getFilterInstance(this.params.dataColumn.field_name);
    this.populateActiveFilterValues();
    this.evalSortedDistributionsDesc();
  }

  onMenuClick() {
    if (this.customHeaderMenuButton) {
      this.params.showColumnMenu(this.customHeaderMenuButton.nativeElement);
    }
  }

  onSortRequested(event: MouseEvent) {
    if (!this.params.enableSorting) {
      return;
    }
    const sorted = this.sorted$.value;
    const sortTarget = sorted === 'asc' ? 'desc' : sorted === 'desc' ? null : 'asc';
    this.params.setSort(sortTarget, event.shiftKey);
  }

  onSortChanged() {
    const sortMode = this.params.column.isSortAscending()
      ? 'asc'
      : this.params.column.isSortDescending()
      ? 'desc'
      : null;
    this.sorted$.next(sortMode);
  }

  setFilter(value: DistributionModel['value']) {
    this.activeFilterValues = [value];

    let model: { type: 'blank' | 'equals'; values: unknown[] } = {
      type: 'blank',
      values: [null],
    };

    if (this.activeFilterValues[0] !== null) {
      model = {
        type: 'equals',
        values: this.activeFilterValues,
      };
    }

    /*
    For boolean columnType on client side (non-SSRM) we use agSetColumnFilter that normalize
    data via stringifying. We need to follow this approach when setting model.
    */
    const dataType = this.params.dataColumn.data_type;
    if (dataType === ColumnTypeEnum.Boolean && model.type === 'equals') {
      model.values = model.values.map((val) => `${val}`);
    }

    /* activeFilterInstance can be one of custom filters: DateClientSideFilterComponent, OperationInputAgFilterComponent
     OR one of built-in ag-grid simple filters, i.e.: agTextColumnFilter, agSetColumnFilter, agNumberColumnFilter
     Those simple filters accepts "value" as "filter" property, so we need to explictly pass it.
    */
    this.activeFilterInstance?.setModel({ ...model, filter: model.values?.[0] ?? null });
    this.params.api.onFilterChanged();
  }

  resetFilter() {
    this.activeFilterValues = [];
    this.activeFilterInstance?.setModel(null);
    this.params.api.onFilterChanged();
  }

  refresh(params: HeaderRendererComponentParams): boolean {
    this.params = params;
    this.evalSortedDistributionsDesc();
    return true;
  }

  analyzeColumn() {
    this.params.context.componentParent.analyzeColumn(this.params.dataColumn.field_name);
  }

  private onFilterChanged() {
    this.filterActive$.next(this.params.column.isFilterActive());
    this.activeFilterInstance = this.params.api.getFilterInstance(this.params.dataColumn.field_name);
    this.populateActiveFilterValues();
    this.evalSortedDistributionsDesc();
  }

  private evalSortedDistributionsDesc() {
    this.sortedDistributionsDesc$.next(sortBy(this.params.distributions || [], ['sample_size']).reverse());
  }

  private checkActive(value: DistributionModel['value']): boolean {
    const operator = this.getUnifiedModel()?.operator;
    let valFormatted = value;
    const dataType = this.params.dataColumn.data_type;
    const dateDataType = [ColumnTypeEnum.Date, ColumnTypeEnum.Datetime, ColumnTypeEnum.Timestamp].includes(dataType);
    const boolDataType = dataType === ColumnTypeEnum.Boolean;

    if (valFormatted !== null) {
      try {
        if (dateDataType) {
          valFormatted = new Date(value as string).toISOString();
        }
      } catch (err) {
        this.errorHandlerService.handleError(err);
        valFormatted = 'Invalid';
      }
    }

    let activeFiltersNormalized = this.activeFilterValues;
    /*
    For boolean columnType on client side (non-SSRM) we use agSetColumnFilter that normalize
    data via stringifying. We need to follow this approach when setting model.
    */
    if (boolDataType) {
      valFormatted = `${valFormatted}`;
      activeFiltersNormalized = this.activeFilterValues.map((val) => `${val}`);
    }

    return (
      (operator === OperatorEnum.Equals || operator === OperatorEnum.IsEmpty) &&
      activeFiltersNormalized &&
      (dateDataType
        ? isNotNullish(
            activeFiltersNormalized.find((activeFilter) => {
              if (dataType === ColumnTypeEnum.Date) {
                return this.dateTimeService.isSame(
                  new Date(activeFilter as string),
                  new Date(valFormatted as string),
                  'days',
                );
              } else if ([ColumnTypeEnum.Datetime, ColumnTypeEnum.Timestamp].includes(dataType)) {
                return new Date(activeFilter as string).getTime() === new Date(valFormatted as string).getTime();
              }

              return false;
            }),
          )
        : activeFiltersNormalized.indexOf(valFormatted) !== -1)
    );
  }

  private tooltipValueFormatter(dist: DistributionModel) {
    const finalFormat = (val: DistributionModel['value']) => `${val}  - n: ${dist.sample_size}`;

    if (!dist.value) {
      return finalFormat(dist.value);
    }

    const valNotBoolean = dist.value as string | Date | number;
    const dataType = this.params.dataColumn.data_type;
    if (dataType === ColumnTypeEnum.Date) {
      return finalFormat(this.dateTimeService.formatDate(valNotBoolean, DateTimeFormat.ShortDate));
    } else if ([ColumnTypeEnum.Datetime, ColumnTypeEnum.Timestamp].includes(dataType)) {
      return finalFormat(new Date(valNotBoolean).toISOString());
    }

    return finalFormat(dist.value);
  }

  // we want to populate filter value set directly in filter widget AKA "customHeaderMenuButton"
  private populateActiveFilterValues() {
    if (!this.filterActive$.value) {
      this.activeFilterValues = [];
      return;
    }

    const onlyOneOperation = this.getUnifiedModel();
    if (!onlyOneOperation) {
      this.activeFilterValues = [];
      return;
    }

    const operator = onlyOneOperation.operator;
    if (operator === OperatorEnum.Equals) {
      const values = onlyOneOperation.values;
      this.activeFilterValues = values as Array<DistributionModel['value'] | null>;
      return;
    } else if (operator === OperatorEnum.IsEmpty) {
      this.activeFilterValues = [null];
      return;
    }

    this.activeFilterValues = [];
  }

  private getUnifiedModel(): { operator: OperationModel['operator']; values: OperationModel['values'] } | null {
    const fieldName = this.params.dataColumn.field_name;
    const filterModel = this.params.api.getFilterModel(); // it can be model from ag-grid simple filter or our operation-input model

    if (!fieldName || !filterModel || !filterModel[fieldName]) {
      return null;
    }

    const model = filterModel[fieldName];
    const modelTypePreNormalized = model.type || model.filterType;
    const modelType = modelTypePreNormalized === 'set' ? 'equals' : modelTypePreNormalized;

    if (modelType) {
      // we have ag-grid simple filter used
      if (model.condition1) {
        // we have more than one condition -> condition1 AND/OR condition2 AND/OR ...
        // we do not handle many filters/operations, only single one -> return null
        return null;
      }

      if (modelType === 'blank') {
        return {
          operator: OperatorEnum.IsEmpty,
          values: [],
        };
      } else if (modelType === 'equals') {
        return {
          operator: OperatorEnum.Equals,
          values: model.values ? model.values : model.filter ? [model.filter] : [model.value],
        };
      }

      return null;
    } else if (model.operations) {
      // we have operations input used
      if (!model.operations || model.operations.length !== 1) {
        return null; // we handle only single exact operation/filter
      }

      return model.operations[0];
    }

    return null;
  }
}
